import _, { result } from 'lodash'
import * as THREE from 'three'
import { UserError } from '@/utils/errors'
import { round } from 'mathjs'

import GlassSegment from '@/model/glassSegment'
import Rectangle from '@/model/rectangle'

import { AlignmentTypes, SegmentTypes, HoleMeasurePointTypes } from '@/store/mathTypes'

let EPSILON = 0.0000001

/**
 * Maths Utility Functions common to the entire frontend
 */
export default class MathsUtils {

  /**
   * Generate a THREE.Shape object from an array of GlassSegment objects
   * Used for generating the pathing for shapes/holes
   */
  static generateShapeFromSegmentList(segmentList, options = {}) {
    var outputShape = new THREE.Shape()

    var isHole = !!options.isHole
    var isOpenShape = !!options.isOpenShape
    let ignoreCornerRadius = !!options.ignoreCornerRadius

    segmentList.forEach((segment, index) => {

      // Initial Point
      if(index == 0) {
        if(segment.type == SegmentTypes.line) {
          if(segment.startCornerRadius == 0.0 || ignoreCornerRadius) {
            outputShape.moveTo(segment.startPosition.x, segment.startPosition.y)
          } else {
            // Adjust for the corner radius
            let roundCornerData = MathsUtils.calculateSegmentDeltaCornerRounding(segment, true, isHole)

            if(!roundCornerData) {
              outputShape.moveTo(segment.startPosition.x, segment.startPosition.y)
            } else {
              // Open shapes start with arc
              // Otherwise skip it as the segment will contain the end corner radius arc below
              if(isOpenShape) {
                outputShape.absarc(roundCornerData.center.x,
                                  roundCornerData.center.y,
                                  roundCornerData.radius,
                                  roundCornerData.startAngle,
                                  roundCornerData.endAngle,
                                  roundCornerData.clockwise)
              } else {
                outputShape.moveTo(roundCornerData.segment2Start.x, roundCornerData.segment2Start.y)
              }
            }
          }
        }
      }

      if(segment.type == SegmentTypes.line) {
        if(segment.endCornerRadius == 0.0 || ignoreCornerRadius)
          outputShape.lineTo(segment.endPosition.x, segment.endPosition.y)
        else {
          // Adjust for the corner radius
          let roundCornerData = MathsUtils.calculateSegmentDeltaCornerRounding(segment, false, isHole)
          
          if(!roundCornerData) {
            outputShape.lineTo(segment.endPosition.x, segment.endPosition.y)
          } else {
            outputShape.lineTo(roundCornerData.segment1End.x, roundCornerData.segment1End.y)
            outputShape.absarc(roundCornerData.center.x,
                              roundCornerData.center.y,
                              roundCornerData.radius,
                              roundCornerData.startAngle,
                              roundCornerData.endAngle,
                              roundCornerData.clockwise)
          }
        }

      } else if(segment.type == SegmentTypes.arc) {
        // TODO: Rounded corner into an arc (chicane). Need to check if required

        outputShape.absarc(
          segment.arcCenterPosition.x,
          segment.arcCenterPosition.y,
          segment.arcRadius,
          MathsUtils.degreesToRadians(segment.arcStartAngle),
          MathsUtils.degreesToRadians(segment.arcEndAngle),
          !segment.arcClockwise     // NOTE: See notes in glassSegment.js for explanation
        )
      }
    })
    return outputShape
  }


  /**
   * Generate a THREE.Shape object from a single segment
   * Used for generating the pathing for a single isolated segment
   * Requires the previous/next segment links for corner radius calculations
   */
  static generateShapeFromSegment(prevSegment, segment, nextSegment) {
    var outputShape = new THREE.Shape()

    if(segment.type == SegmentTypes.line) {
      if(segment.startCornerRadius == 0.0) {
        outputShape.moveTo(segment.startPosition.x, segment.startPosition.y)
      } else {
        // Adjust for the corner radius
        let roundCornerData = MathsUtils.calculateCornerRounding(
          prevSegment.startPosition,
          prevSegment.endPosition,
          segment.startPosition,
          segment.endPosition,
          segment.startCornerRadius,
          false
        )

        if(!roundCornerData) {
          outputShape.moveTo(segment.startPosition.x, segment.startPosition.y)
        } else {
          outputShape.moveTo(roundCornerData.segment2Start.x, roundCornerData.segment2Start.y)
        }
      }
    }

    if(segment.type == SegmentTypes.line) {
      if(segment.endCornerRadius == 0.0)
        outputShape.lineTo(segment.endPosition.x, segment.endPosition.y)
      else {
        // Adjust for the corner radius
        let roundCornerData = MathsUtils.calculateCornerRounding(
          segment.startPosition,
          segment.endPosition,
          nextSegment.startPosition,
          nextSegment.endPosition,
          segment.endCornerRadius,
          false
        )

        if(!roundCornerData) {
          outputShape.lineTo(segment.endPosition.x, segment.endPosition.y)
        } else {
          outputShape.lineTo(roundCornerData.segment1End.x, roundCornerData.segment1End.y)
          outputShape.absarc(roundCornerData.center.x,
                            roundCornerData.center.y,
                            roundCornerData.radius,
                            roundCornerData.startAngle,
                            roundCornerData.endAngle,
                            roundCornerData.clockwise)
        }
      }

    } else if(segment.type == SegmentTypes.arc) {
      // TODO: Rounded corner into an arc (chicane). Need to check if required

      outputShape.absarc(
        segment.arcCenterPosition.x,
        segment.arcCenterPosition.y,
        segment.arcRadius,
        MathsUtils.degreesToRadians(segment.arcStartAngle),
        MathsUtils.degreesToRadians(segment.arcEndAngle),
        !segment.arcClockwise     // NOTE: See notes in glassSegment.js for explanation
      )
    }

    return outputShape
  }


  /**
   * Generate a segment list into raw 2D points
   * Used for simple polygon orientation / closure checks
   * NOTE: Ignores corner radiuses
   * NOTE: Assumes the segment has a valid & updated arc ellipse calculated
   */
  static convertSegmentListToPointList(segmentList) {

    var pointList = []
    segmentList.forEach((segment, index) => {
      if(segment.type == SegmentTypes.line) {
        pointList.push(segment.startPosition.clone())

      } else if(segment.type == SegmentTypes.arc) {
        for(var t=0; t<1.0; t += 0.1)
          pointList.push(segment.arcEllipseCurve.getPoint(t, new THREE.Vector2()))
      }

      // End position if last segment in the list
      if(index == segmentList.length-1)
        pointList.push(segment.endPosition.clone())
    })
    return pointList
  }


  /**
   * Generate a segments start or end corner radius into an arc segment
   * Note: Corner delta data MUST be set on the segment
   */
  static generateSegmentCornerRadiusArc(segment, isStart, isHole = false) {
    if(isStart) {
      if(segment.startCornerRadius <= 0)
        return null

      let roundCornerData = MathsUtils.calculateSegmentDeltaCornerRounding(segment, true, isHole)
      if(roundCornerData) {
        // Add in an arc segment to replace the starting corner radius
        let arcSegment = new GlassSegment(SegmentTypes.arc)
        arcSegment.arcRadius = roundCornerData.radius
        arcSegment.arcCenterPosition.copy(roundCornerData.center)
        arcSegment.startPosition.copy(roundCornerData.segment1End)
        arcSegment.endPosition.copy(roundCornerData.segment2Start)

        arcSegment.arcStartAngle = MathsUtils.calculateArcPointAngle(arcSegment.arcCenterPosition, arcSegment.startPosition)
        arcSegment.arcEndAngle = MathsUtils.calculateArcPointAngle(arcSegment.arcCenterPosition, arcSegment.endPosition)
        arcSegment.arcClockwise = !roundCornerData.clockwise

        arcSegment.isArcFromCornerRadius = true
        arcSegment.arcUpdated()
        return arcSegment
      }
    }
    else {
      if(segment.endCornerRadius <= 0)
        return null

      let roundCornerData = MathsUtils.calculateSegmentDeltaCornerRounding(segment, false, isHole)
      if(roundCornerData) {
        // Add in an arc segment to replace the ending corner radius
        let arcSegment = new GlassSegment(SegmentTypes.arc)
        arcSegment.arcRadius = roundCornerData.radius
        arcSegment.arcCenterPosition.copy(roundCornerData.center)
        arcSegment.startPosition.copy(roundCornerData.segment1End)
        arcSegment.endPosition.copy(roundCornerData.segment2Start)

        arcSegment.arcStartAngle = MathsUtils.calculateArcPointAngle(arcSegment.arcCenterPosition, arcSegment.startPosition)
        arcSegment.arcEndAngle = MathsUtils.calculateArcPointAngle(arcSegment.arcCenterPosition, arcSegment.endPosition)
        arcSegment.arcClockwise = !roundCornerData.clockwise

        arcSegment.isArcFromCornerRadius = true
        arcSegment.arcUpdated()
        return arcSegment
      }
    }

    return null
  }


  /**
   * Convert a segment list, converting start corner radiuses into seperate arc segments
   * Sets 'isArcFromCornerRadius' flag on GlassSegment for converted arcs
   * If isHole is set, corner clockwise windings are flipped
   * 
   * NOTE: Assumes the segment list is CLOSED, and only converts start corners
   */
  static convertSegmentListCornerRadiusToArcs(segmentList, isHole = false) {
    var outputList = []

    segmentList.forEach((segment, index) => {
      let outputSeg = segment.clone()

      if(outputSeg.type == SegmentTypes.line) {
        if(outputSeg.startCornerRadius > 0) {
          let roundCornerData = MathsUtils.calculateSegmentDeltaCornerRounding(outputSeg, true, isHole)
          if(roundCornerData) {

            // Add in an arc segment to replace the starting corner radius
            var arcSegment = new GlassSegment(SegmentTypes.arc)
            arcSegment.arcRadius = roundCornerData.radius
            arcSegment.arcCenterPosition.copy(roundCornerData.center)
            arcSegment.startPosition.copy(roundCornerData.segment1End)
            arcSegment.endPosition.copy(roundCornerData.segment2Start)

            arcSegment.arcStartAngle = MathsUtils.calculateArcPointAngle(arcSegment.arcCenterPosition, arcSegment.startPosition)
            arcSegment.arcEndAngle = MathsUtils.calculateArcPointAngle(arcSegment.arcCenterPosition, arcSegment.endPosition)
            arcSegment.arcClockwise = !roundCornerData.clockwise

            arcSegment.isArcFromCornerRadius = true
            arcSegment.arcUpdated()

            // Clear start corner out so it's fully replaced by the arc
            outputSeg.startPosition.copy(arcSegment.endPosition)
            outputSeg.startCornerRadius = 0

            outputList.push(arcSegment)
          }
        }

        if(outputSeg.endCornerRadius > 0) {
          // Pull back the segment
          let roundCornerData = MathsUtils.calculateSegmentDeltaCornerRounding(outputSeg, false, isHole)
          if(roundCornerData) {
            // Clear end corner out so it's fully replaced by the arc in the next segment
            outputSeg.endCornerRadius = 0
            outputSeg.endPosition.copy(roundCornerData.segment1End)
          }
        }
      }

      outputList.push(outputSeg)
    })

    return outputList
  }


  /**
   * Called when a segment list has been updated
   * Pre-calculates the corner radius details for each vertex in the segment list
   * Segment list must be closed for the calculation
   */
  static segmentListUpdated(segmentList) {
    if(segmentList.length <= 1)
      return

    if(segmentList[0].startPosition.distanceTo(segmentList[segmentList.length-1].endPosition) > 0.5)
      throw new Error('Segment list is not closed')

    segmentList.forEach((segment, segmentIndex) => {
      let prevSeg = segmentList[segmentIndex == 0 ? segmentList.length-1 : segmentIndex-1]
      let nextSeg = segmentList[(segmentIndex + 1) % segmentList.length]

      // Update the corner radius details
      segment.startCornerRadiusDelta = prevSeg.startPosition.clone().sub(segment.startPosition)
      segment.endCornerRadiusDelta = nextSeg.endPosition.clone().sub(segment.endPosition)
    })
  }


  /**
   * Calculate the corner rounding details between 2 glass segments
   * Uses the startCornerRadius of the 2nd segment
   *
   * Returns null if the corner cannot be calculated or has invalid details
   */
  static calculateSegmentCornerRounding(segment1, segment2, isHole = false) {
    return MathsUtils.calculateCornerRounding(
      segment1.startPosition,
      segment1.endPosition,
      segment2.startPosition,
      segment2.endPosition,
      segment2.startCornerRadius,
      isHole
    )
  }


  /**
   * Calculate the corner details for a non-rounded corner between 2 glass segments
   * All angles returned are in radians
   */
  static calculateSegmentNonRoundedCornerDetails(segment1, segment2, isHole = false) {
    var primaryDirection = segment1.endPosition.clone().sub(segment1.startPosition)
    primaryDirection.normalize()
    primaryDirection.negate()
    var secondaryDirection = segment2.endPosition.clone().sub(segment2.startPosition)
    secondaryDirection.normalize()

    var midDirection = primaryDirection.clone()
    midDirection.normalize()
    midDirection.add(secondaryDirection)
    midDirection.normalize()

    var primaryDirDegrees = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(primaryDirection)))
    var secondDirDegrees = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(secondaryDirection)))
    var midDirDegrees = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(midDirection)))

    var isClockwise = !isHole
    var startAngle = secondDirDegrees
    var endAngle = primaryDirDegrees
    var midAngle = midDirDegrees

    let angleDist = MathsUtils.calculateShortestAngleDistanceDegrees(endAngle, startAngle)

    // Adjust / flip angle to be on the correct side
    if(startAngle > endAngle) {
      if(endAngle <= midAngle && startAngle >= midAngle) {
        let tmp = startAngle
        startAngle = endAngle
        endAngle = tmp
        isClockwise = !isClockwise
      } else {
        endAngle += 360
      }
    } else {
      if(!(startAngle <= midAngle && endAngle >= midAngle)) {
        startAngle = endAngle
        endAngle = startAngle + angleDist
        isClockwise = !isClockwise
      }
    }

    let arcPercent = Math.abs(angleDist) / 360.0

    return {
      startAngle: MathsUtils.degreesToRadians(startAngle),
      endAngle: MathsUtils.degreesToRadians(endAngle),
      angle: MathsUtils.degreesToRadians(angleDist),
      center: segment1.endPosition.clone(),
      arcPercent: arcPercent,
      clockwise: isClockwise
    }
  }


  /**
   * Calculates the corner rounding details between 2 segments using a radius
   * Calculates the rounding info between the end of the 1st segment & start of the 2nd segment
   * Returns null if the corner cannot be calculated or has invalid details
   * 
   * Based on code found in:
   * https://stackoverflow.com/questions/24771828/algorithm-for-creating-rounded-corners-in-a-polygon
   */
  static calculateCornerRounding(seg1Start, seg1End, seg2Start, seg2End, cornerRadius, isHole = false) {
    if(cornerRadius <= 0.0)
      return null

    var isClockwise = isHole
    let cornerTriArea = MathsUtils.calculateSignedArea(seg1Start, seg1End, seg2End)

    // Calculate info between the 2 segments
    let p1 = seg1Start
    let p2 = seg2End
    let arcMidPoint = new THREE.Vector2(seg1End.x, seg1End.y)

    let d1 = arcMidPoint.clone().sub(p1)
    let d2 = arcMidPoint.clone().sub(p2)
    let angle = (Math.atan2(d1.y, d1.x) - Math.atan2(d2.y, d2.x)) / 2
    let tan = Math.abs(Math.tan(angle))
    let segmentLength = cornerRadius / tan

    let length1 = d1.length()
    let length2 = d2.length()
    let length = Math.min(length1, length2)

    // Adjust radius if the segments are two small
    if(segmentLength > length) {
      segmentLength = length
      cornerRadius = (length * tan)
    }

    // Points of intersection are calculated by the proportion between 
    // the coordinates of the vector, length of vector and the length of the segment.
    let p1Cross = MathsUtils.getCornerProportionPoint(arcMidPoint, segmentLength, length1, d1)
    let p2Cross = MathsUtils.getCornerProportionPoint(arcMidPoint, segmentLength, length2, d2)

    // Calculation of the coordinates of the circle 
    // center by the addition of angular vectors.
    let diff = new THREE.Vector2(arcMidPoint.x * 2 - p1Cross.x - p2Cross.x, arcMidPoint.y * 2 - p1Cross.y - p2Cross.y)

    let diffLength = diff.length()
    if(diffLength == 0.0)
      return null
    let delta = Math.sqrt(segmentLength*segmentLength + cornerRadius*cornerRadius)

    var circlePoint = MathsUtils.getCornerProportionPoint(arcMidPoint, delta, diffLength, diff)

    // StartAngle and EndAngle of arc
    var startAngle = Math.atan2(p1Cross.y - circlePoint.y, p1Cross.x - circlePoint.x)
    var endAngle = Math.atan2(p2Cross.y - circlePoint.y, p2Cross.x - circlePoint.x)
    
    // Flip clockwise setting based on the orientation of the 2 segments
    // Outside outline (not holes) should be clockwise except concave segments
    if(cornerTriArea < 0 && !isClockwise)
      isClockwise = true

    let angleDistRads = Math.abs((Math.abs(endAngle - startAngle) % (Math.PI*2)) - Math.PI)
    var midAngle = angleDistRads * 0.5
    if(isClockwise)
      midAngle += startAngle
    else
      midAngle = startAngle - midAngle
    let curveMidpoint = new THREE.Vector2(circlePoint.x + Math.cos(midAngle)*cornerRadius, circlePoint.y + Math.sin(midAngle)*cornerRadius)


    // Note: Causes issues when used. Seems to work fine as is with Three.js arc functions
    /*
    var sweepAngle = endAngle - startAngle
    if(sweepAngle < 0) {
      startAngle = endAngle
      sweepAngle = -sweepAngle
    }
    if (sweepAngle > Math.PI)
      sweepAngle -= (2 * Math.PI - sweepAngle)
    */


    var cornerInfo = {
      segment1End: p1Cross,
      segment2Start: p2Cross,
      radius: cornerRadius,
      center: new THREE.Vector2(circlePoint.x, circlePoint.y),
      midpoint: curveMidpoint,
      startAngle: startAngle,
      endAngle: endAngle,
      angleDistRads: angleDistRads,
      arcPercent: Math.abs(angleDistRads / (Math.PI*2)),    // Percent of a full circle the arc represents
      clockwise: !isClockwise     // Flip as THREE.js has different view on clockwise. See comments in glassSegment.js
    }


    return cornerInfo
  }


  /**
   * Calculate the cross point of segment info
   * Called internally from calculateCornerRounding()
   * 
   * Returns cross point as Vector2
   */
  static getCornerProportionPoint(point, segmentLength, length, delta) {
    let factor = segmentLength / length

    return new THREE.Vector2((point.x - delta.x * factor), (point.y - delta.y * factor))
  }


  /**
   * Calculate the corner rounding details of a segment at start or end
   * Uses the delta vectors pre-calculated in the GlassSegment object
   * Returns null if the corner cannot be calculated or has invalid details
   */
  static calculateSegmentDeltaCornerRounding(segment, isStart, isHole = false) {
    if(isStart) {
      let seg1Start = segment.startPosition.clone().add(segment.startCornerRadiusDelta)
      let seg1End = segment.startPosition.clone()
      let seg2Start = segment.startPosition.clone()
      let seg2End = segment.endPosition.clone()

      return MathsUtils.calculateCornerRounding(seg1Start, seg1End, seg2Start, seg2End, segment.startCornerRadius, isHole)
    } else {
      let seg1Start = segment.startPosition.clone()
      let seg1End = segment.endPosition.clone()
      let seg2Start = segment.endPosition.clone()
      let seg2End = segment.endPosition.clone().add(segment.endCornerRadiusDelta)

      return MathsUtils.calculateCornerRounding(seg1Start, seg1End, seg2Start, seg2End, segment.endCornerRadius, isHole)
    }
  }


  /**
   * Calculate a linearly interpolated value given a min/max by a fraction
   */
  static lerp(minValue, maxValue, fraction) {
    return (1 - fraction) * minValue + fraction * maxValue;
  }


  /**
   * Calculate the fraction of a value between a min & max value
   * Returns a fraction between 0-1 if the value is between the range 
   * Fraction extends past [0-1] if the value is outside the range & NOT clamped
   */
  static calculateFraction(value, min, max) {
    if(max-min == 0)
      return 0
    return (value - min) / (max-min)
  }


  /**
   * Map a value between a source range into a value converted to a destination range
   */
  static mapValue(value, srcMin, srcMax, dstMin, dstMax) {
    return MathsUtils.lerp(dstMin, dstMax, MathsUtils.calculateFraction(value, srcMin, srcMax))
  }


  /**
   * Clamp a value between a minimum & maximum (inclusive)
   */
  static clampValue(value, min, max) {
    return Math.min(Math.max(value, min), max)
  }


  /**
   * Resize rectangle using aspect fit function to fit into a smaller rectangle
   * Maintains aspect ratio of the source rectangle
   * 
   * @param {Number} srcWidth - Width of the source rectangle
   * @param {Number} srcHeight - Height of the source rectangle
   * @param {Number} dstWidth - Width of the destination rectangle to fit into
   * @param {Number} dstHeight - Height of the destination rectangle to fit into
   * @return {Object} Object containing new width/height properties
   */
  static aspectFitResize(srcWidth, srcHeight, dstWidth, dstHeight) {
    // Aspect Fit Resize
    // The result size has to fit within the destination area
    if(srcWidth != 0 && srcHeight != 0) {
      
      if(srcHeight > srcWidth) {
        let ratio = srcWidth / srcHeight
        let value = dstHeight*ratio
        if(value > dstWidth && ratio != 0)
          dstHeight = dstWidth * (1.0 / ratio)
        else
          dstWidth = value

      } else {
        let ratio = srcHeight / srcWidth
        let value = dstWidth*ratio
        if(value > dstHeight && ratio != 0)
          dstWidth = dstHeight * (1.0 / ratio)
        else
          dstHeight = value
      }
    }

    return {
      width: dstWidth,
      height: dstHeight
    }
  }


  /**
   * Check for an intersection between 2 arrays of GlassSegment
   * Returns an array of all the intersection points
   * Returns an empty array if no intersections occured
   */
  static checkGlassSegmentListsIntersections(segmentList1, segmentList2) {
    var resultIntersections = []
    segmentList1.forEach(seg1 => {
      segmentList2.forEach(seg2 => {
        let ints = seg1.calculateGlassSegmentIntersection(seg2)
        resultIntersections = resultIntersections.concat(ints)
      })
    })

    return resultIntersections
  }


  /**
   * Check if a segment list self intersects with itself
   */
  static checkGlassSegmentListSelfIntersects(segmentList) {
    let endPointThreshold = 0.1

    // Ignores neighbouring segments
    for(var i=0; i<segmentList.length; i++) {
      let segment = segmentList[i]

      for(var n=i+2; n<segmentList.length; n++) {
        let otherSegment = segmentList[n]

        // Ignore if the start/end points are touching. That is required
        if(segment.startPosition.distanceTo(otherSegment.startPosition) < endPointThreshold)
          continue
        if(segment.endPosition.distanceTo(otherSegment.startPosition) < endPointThreshold)
          continue
        if(segment.startPosition.distanceTo(otherSegment.endPosition) < endPointThreshold)
          continue
        if(segment.endPosition.distanceTo(otherSegment.endPosition) < endPointThreshold)
          continue

        if(segment.calculateGlassSegmentIntersection(otherSegment).length > 0)
          return true
      }
    }
    return false
  }


  /**
   * Check if two 2D line segments intersect each other
   * If an intersection occurs, sets the intersection point arg
   * Returns true if an intersection occurs
   */
  static checkSegmentSegmentIntersection(seg1Start, seg1End, seg2Start, seg2End, outIntPoint = null, endPointThreshold = 0) {
    let at = (seg2End.x - seg2Start.x) * (seg1Start.y - seg2Start.y) - (seg2End.y - seg2Start.y) * (seg1Start.x - seg2Start.x);
    let bt = (seg1End.x - seg1Start.x) * (seg1Start.y - seg2Start.y) - (seg1End.y - seg1Start.y) * (seg1Start.x - seg2Start.x);
    let b  = (seg2End.y - seg2Start.y) * (seg1End.x - seg1Start.x) - (seg2End.x - seg2Start.x) * (seg1End.y - seg1Start.y);

    if(b != 0) {
      let ua = at / b;
      let ub = bt / b;

      if(0.0 <= ua && ua <= 1.0 && 0.0 <= ub && ub <= 1.0) {
        var intPoint = new THREE.Vector2(seg1Start.x + ua * (seg1End.x - seg1Start.x), seg1Start.y + ua * (seg1End.y - seg1Start.y))
        
        // If threshold set & the intersection point is close to an endpoint of a segment, ignore the intersection
        if(endPointThreshold > 0) {
          if(intPoint.distanceTo(seg1Start) <= endPointThreshold ||
             intPoint.distanceTo(seg1End) <= endPointThreshold ||
             intPoint.distanceTo(seg2Start) <= endPointThreshold ||
             intPoint.distanceTo(seg2End) <= endPointThreshold)
            return false          
        }

        if(outIntPoint)
          outIntPoint.copy(intPoint)

        return true
      }
    }
    return false
  }


  /**
   * Check if a 2D line segments intersect with a 2D polygon made up of points
   * 2D Polygon should not be closed
   * If an intersection occurs, sets the intersection point arg
   * Returns true if an intersection occurs
   */
  static checkSegment2DPolygonIntersection(segStart, segEnd, polygonPointList, outIntPoint = null) {
    for(var i=0; i<polygonPointList.length; i++) {
      if(MathsUtils.checkSegmentSegmentIntersection(segStart, segEnd, polygonPointList[i], polygonPointList[(i+1) % polygonPointList.length], outIntPoint))
        return true
    }
    return false
  }


  /**
   * Calculate the angle between 2 segments
   * Assumes both segments are lines
   * Returns shortest angle (in degrees) of the area where the segments join
   * Returns 0 if the segments are not connected at a common point
   */
  static calculateSegmentAngleDistance(segment1, segment2) {
    var seg1Dir = segment1.getDirection().negate()
    var seg2Dir = segment2.getDirection()

    if(!MathsUtils.check2DPointsEqual(segment1.endPosition, segment2.startPosition)) {
      if(MathsUtils.check2DPointsEqual(segment1.startPosition, segment2.startPosition))
        seg1Dir.negate()
      else if(MathsUtils.check2DPointsEqual(segment1.endPosition, segment2.endPosition))
        seg2Dir.negate()
      else
        return 0
    }

    let endAngle = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(seg1Dir)))
    var startAngle = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(seg2Dir)))

    return MathsUtils.roundNumber(MathsUtils.calculateShortestAngleDistanceDegrees(endAngle, startAngle), 2)
  }


  /**
   * Calculate the angle of a point for an arc centre position
   * Calculates the angle on a clockwise circle
   * Returns an angle in degrees
   */
  static calculateArcPointAngle(arcCenterPosition, point) {
    return MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(Math.atan2(point.y - arcCenterPosition.y, point.x - arcCenterPosition.x)))
  }


  /**
   * Calculate the 2D tangent vector of a point on an arc
   * Does NOT clamp the point if it extends beyond the arcs start/end angles
   */
  static calculateArcPointTangent(arcCenterPosition, point) {
    let pointAngle = MathsUtils.calculateArcPointAngle(arcCenterPosition, point)

    return MathsUtils.generate2DDirectionVector(MathsUtils.degreesToRadians(pointAngle + 90))
  }


  /**
   * Get the intersections between a 2D line segment and a circle
   * Returns an array of 0 - 2 intersection points
   * If the array is empty, there are no intersections
   * 
   * Based on code from:
   * http://csharphelper.com/blog/2014/09/determine-where-a-line-intersects-a-circle-in-c/
   */
  static checkSegmentCircleIntersection(segStart, segEnd, circleCentrePos, circleRadius) {
    let dx = segEnd.x - segStart.x
    let dy = segEnd.y - segStart.y

    let A = dx * dx + dy * dy
    let B = 2 * (dx * (segStart.x - circleCentrePos.x) + dy * (segStart.y - circleCentrePos.y))
    let C = (segStart.x - circleCentrePos.x) * (segStart.x - circleCentrePos.x) + (segStart.y - circleCentrePos.y) * (segStart.y - circleCentrePos.y) - circleRadius * circleRadius

    let det = B * B - 4 * A * C
    if ((A <= 0.0000001) || (det < 0)) {
        // No intersections
        return []
    }
    else if (det == 0) {
        // One solution
        let t = -B / (2 * A)

        // Check it lies on the segment
        if(t < 0 || t > 1.0)
          return []

        return [
          new THREE.Vector2(segStart.x + t * dx, segStart.y + t * dy)
        ]
    }
    else {
        // Two solutions.
        let ta = (-B + Math.sqrt(det)) / (2 * A)
        let tb = (-B - Math.sqrt(det)) / (2 * A)
        
        // Check either of the results lie on the segment
        var results = []
        if(ta >= 0.0 && ta <= 1.0)
          results.push(new THREE.Vector2(segStart.x + ta * dx, segStart.y + ta * dy))
        if(tb >= 0.0 && tb <= 1.0)
          results.push(new THREE.Vector2(segStart.x + tb * dx, segStart.y + tb * dy))

        return results
    }
  }


  /**
   * Get the intersections between a 2D circle and another 2D circle
   * Returns an array of 0 - 2 intersection points
   * If the array is empty, there are no intersections
   * 
   * Based on code from:
   * https://github.com/williamfiset/Algorithms
   * https://github.com/williamfiset/Algorithms/blob/master/src/main/java/com/williamfiset/algorithms/geometry/CircleCircleIntersectionPoints.js
   */
  static checkCircleCircleIntersection(circleCenterPosA, circleRadiusA, circleCenterPosB, circleRadiusB) {
    var r, R, d, dx, dy, cx, cy, Cx, Cy;
  
    if (circleRadiusA < circleRadiusB) {
      r  = circleRadiusA;  R = circleRadiusB;
      cx = circleCenterPosA.x; cy = circleCenterPosA.y;
      Cx = circleCenterPosB.x; Cy = circleCenterPosB.y;
    } else {
      r  = circleRadiusB; R  = circleRadiusA;
      Cx = circleCenterPosA.x; Cy = circleCenterPosA.y;
      cx = circleCenterPosB.x; cy = circleCenterPosB.y;
    }
    
    // Compute the vector <dx, dy>
    dx = cx - Cx;
    dy = cy - Cy;

    // Find the distance between two points.
    d = Math.sqrt( dx*dx + dy*dy );
    
    // There are an infinite number of solutions
    // Seems appropriate to also return null
    if (d < EPSILON && Math.abs(R-r) < EPSILON) {
      return []
    }
    else if (d < EPSILON) {
      // No intersection (circles centered at the 
      // same place with different size)
      return []
    }
    
    var x = (dx / d) * R + Cx;
    var y = (dy / d) * R + Cy;
    var P = new THREE.Vector2(x, y);
    
    // Single intersection (kissing circles)
    if (Math.abs((R+r)-d) < EPSILON || Math.abs(R-(r+d)) < EPSILON)
      return [P];
    
    // No intersection. Either the small circle contained within 
    // big circle or circles are simply disjoint.
    if ( (d+r) < R || (R+r < d) )
      return [];
    
    var C = new THREE.Vector2(Cx, Cy);
    var angle = (r*r-d*d-R*R)/(-2.0*d*R)
    if(angle >= +1.0) angle = 0
    else if(angle <= -1.0) angle = Math.PI
    else
      angle = Math.acos(angle)

    // Note: The sample code's rotate point function is different to ours
    // Our point rotate function is using diff coord system it appears & ours matches correct rotate code
    var pt1 = MathsUtils.rotate2DPoint(C, P, +angle);
    var pt2 = MathsUtils.rotate2DPoint(C, P, -angle);
    return [pt1, pt2];
  }


  /**
   * Check if a 2D line segment intersects with an arc
   * Returns an array of 0 - 2 intersection points
   * If the array is empty, there are no intersections
   */
  static checkSegmentArcIntersection(segStart, segEnd, arcCentrePos, arcRadius, arcStartAngle, arcEndAngle, arcClockwise) {

    // Check intersections between segment & full circle
    // Then check if results are within arc start/end angles
    let intersections = MathsUtils.checkSegmentCircleIntersection(segStart, segEnd, arcCentrePos, arcRadius)

    return intersections.filter(intPoint => {
      let intAngle = MathsUtils.calculateArcPointAngle(arcCentrePos, intPoint)

      return MathsUtils.angleWithinRangeDegrees(arcStartAngle, arcEndAngle, intAngle, arcClockwise)
    })
  }


  /**
   * Check if a 2D arc intersects with another 2D arc
   * Returns an array of 0 - 2 intersection points
   * If the array is empty, there are no intersections
   */
  static checkArcArcIntersection(
     arcACentrePos, arcARadius, arcAStartAngle, arcAEndAngle, arcAClockwise,
     arcBCentrePos, arcBRadius, arcBStartAngle, arcBEndAngle, arcBClockwise
  ) {

    // Check intersections between 2 full circles
    // Then check if results are within arc start/end angles of both arcs
    let intersections = MathsUtils.checkCircleCircleIntersection(arcACentrePos, arcARadius, arcBCentrePos, arcBRadius)

    return intersections.filter(intPoint => {
      let intAngleA = MathsUtils.calculateArcPointAngle(arcACentrePos, intPoint)
      let intAngleB = MathsUtils.calculateArcPointAngle(arcBCentrePos, intPoint)

      return MathsUtils.angleWithinRangeDegrees(arcAStartAngle, arcAEndAngle, intAngleA, arcAClockwise) &&
        MathsUtils.angleWithinRangeDegrees(arcBStartAngle, arcBEndAngle, intAngleB, arcBClockwise)
    })
  }


  /**
   * Get the closest 2D point on a 2D line segment to another point that is not on a line segment
   * If clamp is false, will extend out past the segment
   * Returns the Vector2 that is on the line segment
   */
  static getClosest2DPointOnLineSegment(segStart, segEnd, point, clamp = true) {
    let ap = new THREE.Vector2(point.x - segStart.x, point.y - segStart.y)
    let ab = new THREE.Vector2(segEnd.x - segStart.x, segEnd.y - segStart.y)

    let lineLengthSq = ab.lengthSq()
    if(lineLengthSq == 0.0)
      return segStart.clone()

    let distance = ap.dot(ab) / lineLengthSq
    if(distance < 0 && clamp)
      return segStart.clone()
    else if(distance > 1 && clamp)
      return segEnd.clone()

    // Interpolate along segment
    return segStart.clone().add(ab.multiplyScalar(distance))
  }


  /**
   * Project a 2D point onto a 2D plane
   * Returns the Vector2 that is the projected point on a plane
   */
  static project2DPointOnPlane(point, planeNormal, planeCoPlanarPoint) {
    return planeNormal.clone().multiplyScalar(-MathsUtils.calculateDistanceToPlane2D(planeNormal, planeCoPlanarPoint, point)).add(point)
  }


  /**
   * Compare two 2D planes (given normal & coplanar points) and see if they are equal
   * Returns true if the two planes have equal normals and both coplanar points are on the same plane
   */
  static comparePlanes(planeANormal, planeAPoint, planeBNormal, planeBPoint, normalThreshold = 0.0001, distThreshold = 0.1) {
    let diffNormal = 1 - planeANormal.dot(planeBNormal)
    let midDistA = Math.abs(MathsUtils.calculateDistanceToPlane2D(planeANormal, planeAPoint, planeBPoint))
    let midDistB = Math.abs(MathsUtils.calculateDistanceToPlane2D(planeBNormal, planeBPoint, planeAPoint))

    return (Math.abs(diffNormal) < normalThreshold && midDistA < distThreshold && midDistB < distThreshold)
  }


  /**
   * Compare two 2D normals to see if they are equal
   * Returns true if the two normals are equal (within a threshold)
   */
  static compareNormals(normalA, normalB, threshold = 0.0001) {
    let diffNormal = 1 - normalA.dot(normalB)
    return (Math.abs(diffNormal) < threshold)
  }


  /**
   * Check if an array of 2D points is a closed shape
   * Returns true if the last point matches the first point
   */
  static is2DPolygonClosed(pointList) {
    if(!pointList || pointList.length <= 2)
      return false

    return Math.abs(pointList[0].x - pointList[pointList.length-1].x) <= EPSILON &&
      Math.abs(pointList[0].y - pointList[pointList.length-1].y) <= EPSILON
  }


  /**
   * Check the orientation of an array of 2D points
   * Polygon should NOT be closed (ie, last point should NOT equal first point)
   * 
   * Based on the algorithm from:
   * http://geomalgorithms.com/
   * 
   * Returns > 0 for counterclockwise
   * Returns 0 for none (degenerate)
   * Returns < 0 for clockwise
   */
  static get2DPolygonOrientation(pointList) {
    if(pointList.length <= 2)
      return 0

    var minIndex = 0
    var xmin = pointList[0].x
    var ymin = pointList[0].y

    pointList.forEach((point, index) => {
      if(index == 0)
        return
      if(point.y > ymin)
        return
      if(point.y == ymin) {
        if(point.x < xmin)
          return
      }
      minIndex = index
      xmin = point.x
      ymin = point.y
    })

    // Run test on the sample point
    if(minIndex == 0)
      return MathsUtils.is2DPointLeftOfLine(pointList[pointList.length-1], pointList[0], pointList[1])
    else
      return MathsUtils.is2DPointLeftOfLine(pointList[minIndex-1], pointList[minIndex], pointList[(minIndex+1) % pointList.length])
  }


  /**
   * Check of an array of 2D points in a path are clockwise
   * Used for non-closed paths (ie, not polygons)
   */
  static is2DPathClockwise(pointList) {
    if(pointList.length <= 2)
      return true

    var edgeSum = 0
    pointList.forEach((point, index) => {
      let nextPoint = index < pointList.length-1 ? pointList[index+1] : pointList[0]

      edgeSum += (nextPoint.x - point.x) * (nextPoint.y + point.y)
    })

    return edgeSum >= 0
  }


  /**
   * Check if a 2D point is inside a 2D polygon
   * Polygon should NOT be closed (ie, last point should NOT equal first point)
   * 
   * Returns true if the point is inside the polygon
   */
  static pointIn2DPolygon(testPoint, pointList) {
    var crossCount = 0
    if(pointList.length == 0)
      return 0

    pointList.forEach((polyPoint, index) => {
      let nextPoint = pointList[(index+1) % pointList.length]

      if(((polyPoint.y <= testPoint.y) && (nextPoint.y > testPoint.y))
      || (polyPoint.y > testPoint.y) && (nextPoint.y <= testPoint.y)) {

        let vt = (testPoint.y - polyPoint.y) / (nextPoint.y - polyPoint.y)
        if(testPoint.x < polyPoint.x + vt * (nextPoint.x - polyPoint.x))
          crossCount++
      }
    })

    return (crossCount & 1) != 0
  }


  /**
   * Test if a point is left / on / right of an infinite 2D line
   * Returns:
   *   >0 for point left of the line
   *   0 for point on the line
   *   <0 for point right of the line
   */
  static is2DPointLeftOfLine(lineStart, lineEnd, point) {

    // Note: Y's inverted to match our coordinate system
    return ((lineEnd.x - lineStart.x) * (lineStart.y - point.y) 
          - (point.x - lineStart.x) * (lineStart.y - lineEnd.y))
  }


  /**
   * Get the closest point on a 2D polygon to a point
   * Returns a Vector2 object with the closest point on the polygon
   * Returns null if the polygon is not valid
   */
  static calculateClosestPointOn2DPolygon(testPoint, pointList) {
    if(pointList.length <= 1)
      return null

    var closestPoint = null
    var closestDist = -1

    pointList.forEach((polyPoint, index) => {
      let nextPoint = pointList[(index+1) % pointList.length]

      var pointOnSeg = MathsUtils.getClosest2DPointOnLineSegment(polyPoint, nextPoint, testPoint, true)
      var dist = pointOnSeg.distanceTo(testPoint)
      if(dist < closestDist || !closestPoint) {
        closestPoint = pointOnSeg
        closestDist = dist
      }
    })

    return closestPoint
  }


  /**
   * Get the 2D orientation of a list of GlassSegments that represent a shape
   * 
   * Returns > 0 for counterclockwise
   * Returns 0 for none (degenerate)
   * Returns < 0 for clockwise
   */
  static getGlassSegmentListOrientation(segmentList) {
    // Convert to a flat point list
    var pointList = []

    segmentList.forEach(segment => {
      pointList.push(segment.startPosition)

      // If an arc, add in points at 25%, 50%, 75% of the arc to ensure large arcs get properly accounted for
      if(segment.type == SegmentTypes.arc && segment.arcEllipseCurve) {
        pointList.push(segment.arcEllipseCurve.getPoint(0.25, new THREE.Vector2()))
        pointList.push(segment.arcEllipseCurve.getPoint(0.5, new THREE.Vector2()))
        pointList.push(segment.arcEllipseCurve.getPoint(0.75, new THREE.Vector2()))
      }
    })

    return MathsUtils.get2DPolygonOrientation(pointList)
  }


  /**
   * Check if an array of GlassSegments are closed
   * Returns true if the last point matches the first point
   */
  static isSegmentListClosed(segmentList) {
    if(!segmentList || segmentList.length <= 1)
      return false

    return Math.abs(segmentList[0].startPosition.x - segmentList[segmentList.length-1].endPosition.x) <= EPSILON &&
      Math.abs(segmentList[0].startPosition.y - segmentList[segmentList.length-1].endPosition.y) <= EPSILON
  }


  /**
   * Reverse a list of GlassSegments
   * Used to reverse the winding of the list of GlassSegments
   * First segment start position remains the same
   * Mutates the segment list in place & also returns the mutated list
   */
  static reverseGlassSegmentList(segmentList) {
    var segmentCopyList = segmentList.map(s => s.clone())

    for(var i=0; i<segmentList.length; i++) {
      segmentList[i].id = segmentCopyList[segmentList.length-i-1].id
      
      segmentList[i].type = segmentCopyList[segmentList.length-i-1].type
      segmentList[i].arcCenterPosition = segmentCopyList[segmentList.length-i-1].arcCenterPosition
      segmentList[i].arcRadius = segmentCopyList[segmentList.length-i-1].arcRadius
      segmentList[i].arcStartAngle = segmentCopyList[segmentList.length-i-1].arcEndAngle
      segmentList[i].arcEndAngle = segmentCopyList[segmentList.length-i-1].arcStartAngle
      segmentList[i].arcClockwise = !segmentCopyList[segmentList.length-i-1].arcClockwise

      segmentList[i].startPosition = segmentCopyList[segmentList.length-i-1].endPosition
      segmentList[i].endPosition = segmentCopyList[segmentList.length-i-1].startPosition

      segmentList[i].startCornerRadius = segmentCopyList[segmentList.length-i-1].endCornerRadius
      segmentList[i].endCornerRadius = segmentCopyList[segmentList.length-i-1].startCornerRadius

      segmentList[i].startCornerRadiusDelta = segmentCopyList[segmentList.length-i-1].endCornerRadiusDelta
      segmentList[i].endCornerRadiusDelta = segmentCopyList[segmentList.length-i-1].startCornerRadiusDelta

      segmentList[i].isUserFixed = segmentCopyList[segmentList.length-i-1].isUserFixed
      
      segmentList[i].isEdgeworkAvailable = segmentCopyList[segmentList.length-i-1].isEdgeworkAvailable
      segmentList[i].orderEditorEdgeworkConfigId = segmentCopyList[segmentList.length-i-1].orderEditorEdgeworkConfigId
      segmentList[i].edgeworkSettingValue = segmentCopyList[segmentList.length-i-1].edgeworkSettingValue

      segmentList[i].isCutoutPolished = segmentCopyList[segmentList.length-i-1].isCutoutPolished
      segmentList[i].isStartCornerPolished = segmentCopyList[segmentList.length-i-1].isStartCornerPolished

      if(segmentList[i].type == SegmentTypes.arc)
        segmentList[i].arcUpdated()
    }

    return segmentList    
  }


  /**
   * Inserts an open custom shape into another shape
   * Uses segment lists for both shapes
   * Used for custom edge/corner cutouts that have open shapes with a start & end vertex
   * that needs to be inserted into the target shape
   *
   * @return {Object} Returns an object contains results of the clipping. Including properties:
   *    segmentList - Array of GlassSegments of the clipping result
   *    startPosition - Vector2 of the start point of the clipping on the target list
   *    endPosition - Vector2 of the end point of the clipping on the target list
   */
  static insertCustomOpenShapeToShape(targetShapeSegmentList, openShapeSegmentList) {
    if(targetShapeSegmentList.length < 2)
      throw new UserError('Target shape must have at least 2 edges')
    if(openShapeSegmentList.length < 1)
      throw new UserError('Inserted shape must have at least 1 edge')

    var openList = MathsUtils.reverseGlassSegmentList(openShapeSegmentList.map(segment => segment.clone()))
    var destList = targetShapeSegmentList.map(segment => segment.clone())

    let startVert = openList[0].startPosition
    let endVert = openList[openList.length - 1].endPosition

    // Find the closest segment to the opening vertex of the open shape & start there
    var startClosestDist = 0
    var startIntAlpha = 0
    var endClosestDist = 0
    var startSegmentIndex = -1
    var endIntAlpha = 0
    var endSegmentIndex = -1

    destList.forEach((segment, index) => {
      var startNearestPnt = segment.projectPointOnSegment(startVert)
      var startDist = startNearestPnt.distanceTo(startVert)
      var endNearestPnt = segment.projectPointOnSegment(endVert)
      var endDist = endNearestPnt.distanceTo(endVert)

      if(startSegmentIndex == -1 || startDist < startClosestDist) {
        startClosestDist = startDist
        startSegmentIndex = index
        startIntAlpha = segment.calculatePointFractionOnSegment(startVert)
      }
      if(endSegmentIndex == -1 || endDist < endClosestDist) {
        endClosestDist = endDist
        endSegmentIndex = index
        endIntAlpha = segment.calculatePointFractionOnSegment(endVert)
      }
    })
    if(startSegmentIndex == -1)
      throw new UserError('Could not find a point on the shape to insert the custom shape')

    // Start & destination points must be within a couple millimetres of an edge on the target shape
    let minEntryExitDistance = 2.5
    if(startClosestDist > minEntryExitDistance || endClosestDist > minEntryExitDistance)
      throw new UserError('Start and end points of the inserted cutout must touch the target shape')

    // Detect intersections between segments
    // Ignores end points as they should intersect with segments (the connection)
    /*
    // JB: 10/12/2021 - Disabled
    // Some cutouts (like the curved TOP PIVOT HP20) curved outside & back in over the shape segment it replaces
    // We'll disable this for now & revisit if becomes an issue
    // This is valid, as the intersection point would be replaced by the cutout
    // In future, should check if the shape intersects with itself AFTER applying the custom cutout
    destList.forEach(destSegment => {
      openList.forEach(openSegment => {
        if(destSegment.calculateGlassSegmentIntersection(openSegment, minEntryExitDistance).length > 0)
          throw new UserError('Inserted shape edge intersects with the target shape')
      })
    })
    */

    // If start intersection is after end intersection, reverse the open list
    if((startSegmentIndex == endSegmentIndex && startIntAlpha > endIntAlpha) || (startSegmentIndex > endSegmentIndex)) {
      openList = MathsUtils.reverseGlassSegmentList(openList.map(segment => segment.clone()))

      let tmp = startSegmentIndex
      startSegmentIndex = endSegmentIndex
      endSegmentIndex = tmp
    }

    // Special case where cutout is after & before the 1st vertex of the shape
    // Need to reverse operation (keep vs discard)
    // TODO: This has issues if there are multiple vertices going to be clipped out inside the corner
    if(endSegmentIndex - startSegmentIndex > destList.length - endSegmentIndex + startSegmentIndex) {
      let tmp = startSegmentIndex
      startSegmentIndex = endSegmentIndex
      endSegmentIndex = tmp

      openList = MathsUtils.reverseGlassSegmentList(openList.map(segment => segment.clone()))
    }

    var exitEntrySegmentSame = (startSegmentIndex == endSegmentIndex)
    var startSegCopy = destList[startSegmentIndex].clone()

    // Store start/end positions for returning results
    var resultStartPosition = openList[0].startPosition.clone()
    var resultEndPosition = openList[openList.length-1].endPosition.clone()

    destList[startSegmentIndex].endPosition = openList[0].startPosition.clone()
    destList[startSegmentIndex].endCornerRadius = openList[0].startCornerRadius

    // If an arc, stop the arc at the end position
    if(destList[startSegmentIndex].type == SegmentTypes.arc) {
      destList[startSegmentIndex].arcEndAngle = MathsUtils.calculateArcPointAngle(destList[startSegmentIndex].arcCenterPosition, destList[startSegmentIndex].endPosition)
      destList[startSegmentIndex].arcUpdated()
    }

    // If exit / entry is on the same segment, add in the remaining part of the segment
    if(exitEntrySegmentSame) {
      // Insert segments
      openList.forEach(shapeSegment => {
        destList.splice(++startSegmentIndex, 0, shapeSegment.clone())
        endSegmentIndex++
      })
      
      let lastShapeSeg = openList[openList.length-1]
      var joinSegment = startSegCopy
      
      joinSegment.startPosition = lastShapeSeg.endPosition
      joinSegment.startCornerRadius = lastShapeSeg.endCornerRadius

      // If an arc, setup the info
      if(joinSegment.type == SegmentTypes.arc) {
        joinSegment.arcStartAngle = MathsUtils.calculateArcPointAngle(joinSegment.arcCenterPosition, joinSegment.startPosition)
        joinSegment.arcUpdated()
      }

      destList.splice(++startSegmentIndex, 0, joinSegment)
    } else {
      // Otherwise, remove all segments between exit/entry & clean up the end segment
      
      // null out the segments to remove
      // They'll be removed after
      for(var i=0; i<destList.length; i++) {
        if(startSegmentIndex < endSegmentIndex) {
          if(i > startSegmentIndex && i < endSegmentIndex)
            destList[i] = null
        } else {
          if(i > startSegmentIndex || i < endSegmentIndex)
            destList[i] = null
        }
      }

      // Fix up the last segment
      let lastShapeSeg = openList[openList.length-1]
      destList[endSegmentIndex].startPosition = lastShapeSeg.endPosition.clone()
      destList[endSegmentIndex].startCornerRadius = lastShapeSeg.endCornerRadius

      // If an arc, stop the arc at the end position
      if(destList[endSegmentIndex].type == SegmentTypes.arc) {
        destList[endSegmentIndex].arcStartAngle = MathsUtils.calculateArcPointAngle(destList[endSegmentIndex].arcCenterPosition, destList[endSegmentIndex].startPosition)
        destList[endSegmentIndex].arcUpdated()
      }

      // Insert segments
      openList.forEach(shapeSegment => {
        destList.splice(++startSegmentIndex, 0, shapeSegment.clone())
      })      

      destList = _.compact(destList)
    }

    // Attempt to re-use segment IDs from the original target list for matching segments in the new destination list
    destList.forEach(destSegment => {
      let srcSegment = targetShapeSegmentList.find(otherSeg => otherSeg.equalsPositionData(destSegment))
      if(srcSegment)
        destSegment.id = srcSegment.id
    })    

    MathsUtils.segmentListUpdated(destList)

    return {
      segmentList: destList,
      startPosition: resultStartPosition,
      endPosition: resultEndPosition
    }
  }


  /**
   * Convert radians to degrees
   */
  static radiansToDegrees(rads) {
    return rads * (180.0 / Math.PI)
  }

  /**
   * Convert degrees to radians
   */
  static degreesToRadians(degrees) {
    return degrees * (Math.PI / 180.0)
  }


  /**
   * Wrap around an angle (in degrees) so it's between 0-360
   */
  static wrapAngleDegrees(angle) {
    if(isNaN(angle))
      return angle

    while(angle < 0)
      angle += 360
    while(angle >= 360)
      angle -= 360
    return angle
  }


  /**
   * Wrap around an angle (in radians) so it's between -PI <-> +PI
   */
  static wrapAngleRadians(radians) {
    if(isNaN(radians))
      return radians

    while(radians < -Math.PI)
      radians += -Math.PI
    while(radians >= Math.PI)
      radians -= Math.PI
    return radians
  }


  /**
   * Calculates the shortest distance between 2 angles in degrees
   * Returns a value between 0 <-> 180
   * Ignores clockwise / counter-clockwise
   */
  static calculateShortestAngleDistanceDegrees(angle1, angle2) {
    angle1 = MathsUtils.wrapAngleDegrees(angle1)
    angle2 = MathsUtils.wrapAngleDegrees(angle2)

    var dist = Math.abs(angle2 - angle1)
    if(dist >= 180)
      dist = 360 - dist
    return dist
  }


  /**
   * Calculate the distance between start & end angles in degrees using clockwise or counter-clockwise flag
   * Will not get the shortest distance, will always follow the cw/ccw rule
   */
  static calculateAngleDistanceDegrees(startAngle, endAngle, clockwise = true) {
    startAngle = MathsUtils.wrapAngleDegrees(startAngle)
    endAngle = MathsUtils.wrapAngleDegrees(endAngle)

    if(clockwise) {
      if(endAngle >= startAngle)
        return endAngle - startAngle
      else
        return (360 - startAngle) + endAngle
    } else {
      if(endAngle <= startAngle)
        return startAngle - endAngle
      else
        return (360 - endAngle) + startAngle
    }
  }


  /**
   * Check if an angle is between two angles (or equal) using clockwise/counter-clockwise flag
   * All angles in degrees
   * Returns true if the angle is within the range
   */
  static angleWithinRangeDegrees(startAngle, endAngle, testAngle, clockwise = true) {
    startAngle = MathsUtils.wrapAngleDegrees(startAngle)
    endAngle = MathsUtils.wrapAngleDegrees(endAngle)
    testAngle = MathsUtils.wrapAngleDegrees(testAngle)

    if(clockwise) {
      if(endAngle >= startAngle)
        return testAngle >= startAngle && testAngle <= endAngle
      else
        return testAngle >= startAngle || testAngle <= endAngle
    } else {
      if(endAngle <= startAngle)
        return testAngle <= startAngle && testAngle >= endAngle
      else
        return testAngle <= startAngle || testAngle >= endAngle
    }
  }


  /**
   * Map an angle value between a start & end angle in degrees to a fraction using clockwise/counter-clockwise flag
   * Assumes the angle is between the start / end range
   * Returns a 0-1 value converted to the destination range
   * Returns 0 value if the angle is outside the range
   */
  static mapAngleDegreesValue(angle, startAngle, endAngle, clockwise = true) {
    startAngle = MathsUtils.wrapAngleDegrees(startAngle)
    endAngle = MathsUtils.wrapAngleDegrees(endAngle)
    angle = MathsUtils.wrapAngleDegrees(angle)

    if(clockwise) {
      if(endAngle >= startAngle) {
        if(angle >= startAngle && angle <= endAngle)
          return MathsUtils.calculateFraction(angle, startAngle, endAngle)
        else
          return 0
      } else {
        if(angle >= startAngle)
          return MathsUtils.calculateFraction(angle, startAngle, endAngle+360)
        else if(angle <= endAngle)
          return MathsUtils.calculateFraction(angle + 360, startAngle, endAngle+360)
        return 0
      }
    } else {
      if(endAngle <= startAngle) {
        if(angle <= startAngle && angle >= endAngle)
          return 1 - MathsUtils.calculateFraction(angle, endAngle, startAngle)
        else
          return 0
      } else {
        if(angle <= startAngle)
          return 1 - MathsUtils.calculateFraction(angle + 360, endAngle, startAngle + 360)
        else if(angle >= endAngle)
          return 1 - MathsUtils.calculateFraction(angle, endAngle, startAngle+360)
        else
          return 0
      }
    }

  }


  /**
   * Calculate the intersection of a mouse/touch ray with the editing plane
   * Assumes the editing plane is on the 2D, Z=0 plane in Ortho camera
   * 
   * Returns true if the ray intersected with the editing plane
   * Fills in intersectionPoint with the intersection
   */
  static editingPlaneIntersection(ray, intersectionPoint) {
    var editingPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
    return ray.intersectPlane(editingPlane, intersectionPoint)
  }


  /**
   * Calculate the distance from a 2D plane (normal + point on plane) to a 2D position
   */
  static calculateDistanceToPlane2D(normal, planeCoPlanarPoint, point) {
    
    return normal.dot(point) - planeCoPlanarPoint.dot(normal)
  }


  /**
   * Calculate if a 2D line segment intersects with a 2D plane (normal + point on plane)
   * Returns the intersection point, or null if no intersection occurs
   */
  static calculate2DLineSegmentPlaneIntersection(lineStart, lineEnd, planeNormal, planeCoPlanarPoint) {

    let dist1 = MathsUtils.calculateDistanceToPlane2D(planeNormal, planeCoPlanarPoint, lineStart)
    let dist2 = MathsUtils.calculateDistanceToPlane2D(planeNormal, planeCoPlanarPoint, lineEnd)

    if((dist1 < 0 && dist2 < 0) || (dist1 > 0 && dist2 > 0))
      return null

    // Intersection. Calculate point
    let length = dist2 - dist1
    if(length == 0.0)
      return null
    let fraction = -dist1 / length

    return new THREE.Vector2(lineStart.x + (lineEnd.x - lineStart.x) * fraction, lineStart.y + (lineEnd.y - lineStart.y) * fraction)
  }


  /**
   * Calculate the closest corner on a Box2 object to a 2D position
   * Returns a Vector2 object
   */
  static calculateClosestBoxCornerToPosition2D(box, point) {
    if(box.isEmpty())
      return new THREE.Vector2(point.x, point.y)

    let cx = (box.min.x + box.max.x) / 2.0
    let cy = (box.min.y + box.max.y) / 2.0

    if(point.x < cx) {
      if(point.y < cy)
        return new THREE.Vector2(box.min.x, box.min.y)
      else
        return new THREE.Vector2(box.min.x, box.max.y)
    } else {
      if(point.y < cy)
        return new THREE.Vector2(box.max.x, box.min.y)
      else
        return new THREE.Vector2(box.max.x, box.max.y)
    }
  }


  /**
   * Calculate the closest point anywhere along the edges of a Box2 object to a 2D position
   * Returns a Vector2 object
   */
  static calculateClosestBoxEdgePointToPosition2D(box, point) {
    if(box.isEmpty())
      return new THREE.Vector2(point.x, point.y)

    let corners = [
      new THREE.Vector2(box.min.x, box.min.y),
      new THREE.Vector2(box.max.x, box.min.y),
      new THREE.Vector2(box.max.x, box.max.y),
      new THREE.Vector2(box.min.x, box.max.y)
    ]

    var closestPoint = null
    var closestDist = -1
    corners.forEach((corner, index) => {
      let pnt = MathsUtils.getClosest2DPointOnLineSegment(corner, corners[(index+1) % corners.length], point, true)
      let dist = pnt.distanceTo(point)
      if(dist < closestDist || closestDist < 0) {
        closestPoint = pnt
        closestDist = dist
      }
    })

    return closestPoint
  }


  /**
   * Converts a Box2 object into 9 reference points (corners, middle edge & center)
   * Calculates the closest reference point to a position & returns that
   */
  static calculateClosestBoxReferencePointToPosition2D(box, point) {
    if(box.isEmpty())
      return new THREE.Vector2(point.x, point.y)

    var closestX = 0.0
    var closestY = 0.0
    var closestDist = -1

    // Note: Don't create numerous Vector2 objects for performance
    // Also using square distance (no square root) for performance
    let cx = (box.min.x + box.max.x) / 2.0
    let cy = (box.min.y + box.max.y) / 2.0
    var refPoints = [
      [box.min.x, box.min.y],
      [cx, box.min.y],
      [box.max.x, box.min.y],
      [box.min.x, cy],
      [cx, cy],
      [box.max.x, cy],
      [box.min.x, box.max.y],
      [cx, box.max.y],
      [box.max.x, box.max.y]
    ]
    refPoints.forEach(ref => {
      let dist = (ref[0]-point.x) * (ref[0]-point.x) + (ref[1]-point.y) * (ref[1]-point.y)
      if(dist < closestDist || closestDist < 0) {
        closestDist = dist
        closestX = ref[0]
        closestY = ref[1]
      }
    })

    return new THREE.Vector2(closestX, closestY)
  }


  /**
   * Calculate the minimum distance between edges of two Box2 objects
   * Returns 0 if the Box2 objects intersect
   */
  static calculateBox2Box2MinDistance(box1, box2) {
    if(box1.intersectsBox(box2))
      return 0

    var horzDist = 0
    var vertDist = 0
    
    if(box1.max.x < box2.min.x)
      horzDist = box2.min.x - box1.max.x
    else if(box2.max.x < box1.min.x)
      horzDist = box1.min.x - box2.max.x

    if(box1.max.y < box2.min.y)
      vertDist = box2.min.y - box1.max.y
    else if(box2.max.y < box1.min.y)
      vertDist = box1.min.y - box2.max.y

    return Math.max(horzDist, vertDist)
  }


  /**
   * Calculate the measurement types for a point relative to a hole's bounds
   * Assumes the measurements are axis aligned
   * Returns the type from HoleMeasurePointTypes
   */
  static calculateHoleMeasureType(holeBounds, point, isHorizontal) {
    let tolerance = 0.5

    if(holeBounds.isEmpty())
      return isHorizontal ? HoleMeasurePointTypes.center : HoleMeasurePointTypes.middle

    if(isHorizontal) {
      if(point.x <= holeBounds.min.x + tolerance)
        return HoleMeasurePointTypes.left
      else if(point.x >= holeBounds.max.x - tolerance)
        return HoleMeasurePointTypes.right

      return HoleMeasurePointTypes.center

    } else {
      if(point.y <= holeBounds.min.y + tolerance)
        return HoleMeasurePointTypes.top
      else if(point.y >= holeBounds.max.y - tolerance)
        return HoleMeasurePointTypes.bottom

      return HoleMeasurePointTypes.middle
    }
  }


  /**
   * Calculate the hole offset point given bounds & the horizontal & vertical measure types
   * Returns a Vector2 for the offset in local space between [0,0] -> [boundMaxX, boundMaxY]
   */
  static calculateHoleOffsetPoint(holeBounds, offsetHorizontalType, offsetVerticalType) {
    var offset = new THREE.Vector2(0, 0)

    switch(offsetHorizontalType) {
      case HoleMeasurePointTypes.left:
        offset.x = 0
        break
      case HoleMeasurePointTypes.center:
        offset.x = (holeBounds.max.x - holeBounds.min.x) / 2
        break
      case HoleMeasurePointTypes.right:
        offset.x = holeBounds.max.x - holeBounds.min.x
        break
    }
    switch(offsetVerticalType) {
      case HoleMeasurePointTypes.top:
        offset.y = 0
        break
      case HoleMeasurePointTypes.middle:
        offset.y = (holeBounds.max.y - holeBounds.min.y) / 2
        break
      case HoleMeasurePointTypes.bottom:
        offset.y = holeBounds.max.y - holeBounds.min.y
        break
    }

    return offset
  }


  /**
   * Round a value by n decimal places
   */
  static roundNumber(value, places = 0) {
    return round(value, places)
  }


  /**
   * Round a THREE Vector2 by n decimal places
   */
  static round2DVector(vec, places = 4) {
    vec.x = round(vec.x, places)
    vec.y = round(vec.y, places)
    return vec
  }


  /**
   * Get the radians of a 2D direction vector
   */
  static get2DVectorAngle(direction) {
    return Math.atan2(direction.y, direction.x)
  }


  /**
   * Generate a 2D direction vector using a simple angle (in radians)
   */
   static generate2DDirectionVector(angleRadians) {
    return new THREE.Vector2(Math.cos(angleRadians), Math.sin(angleRadians))
  }


  /**
   * Generate a 3D direction vector using a simple angle (in radians)
   * Assumes the direction vector is 2D based with Z=0
   */
  static generate3DDirectionVector(angleRadians) {
    return new THREE.Vector3(Math.cos(angleRadians), Math.sin(angleRadians), 0)
  }


  /**
   * Calculate the bounding box of an array of GlassSegments
   * Returns a Box2 object containing bounds of the segments
   */
  static calculateGlassSegmentListBounds(segmentList) {
    var bounds = new THREE.Box2()
    if(segmentList == null)
      return bounds

    segmentList.forEach(segment => {
      let segmentBounds = segment.calculateBounds()

      bounds.union(segmentBounds)
    })

    return bounds
  }


  /**
   * Calculate the closest point on a segment list to another point
   * Returns a Vector2 object of a point along the segment list
   */
  static calculateClosestPointOnSegmentList(segmentList, point) {
    var closestPoint = null
    var closestDist = -1

    segmentList.forEach(segment => {
      let projectedPoint = segment.projectPointOnSegment(point, true)
      let dist = projectedPoint.distanceToSquared(point)
      if(dist < closestDist || closestPoint == null) {
        closestPoint = projectedPoint
        closestDist = dist
      }
    })
    return closestPoint
  }


  /**
   * Check if a point is on a segment within a threshold
   */
  static isPointOnSegment(segment, point, threshold = 0.001) {
    var pointOnSeg = segment.projectPointOnSegment(point, true)
    
    return pointOnSeg.distanceTo(point) < threshold
  }


  /**
   * Calculate the distance along the segment path that a point rests on
   * Based on closest point along a segment list
   * Currently ignores corner radiuses
   * Returns total distance the point on the segment list is from the origin of the segment list
   */
  static calculatePointDistanceAlongSegmentListDistance(segmentList, point) {
    var closestDistanceAlong = 0
    var closestPoint = null
    var closestDist = -1
    var currentDistance = 0

    segmentList.forEach(segment => {
      let projectedPoint = segment.projectPointOnSegment(point, true)
      let dist = projectedPoint.distanceToSquared(point)
      if(dist < closestDist || closestPoint == null) {
        closestPoint = projectedPoint
        closestDist = dist

        let segFraction = segment.calculatePointFractionOnSegment(projectedPoint)
        closestDistanceAlong = currentDistance + segment.getLength() * segFraction
      }

      currentDistance += segment.getLength()
    })

    return closestDistanceAlong
  }


  /**
   * Given a distance along a segmentList path, return the point on the path
   * Distance along must be between zero and the length of the segment list
   * Currently ignores corner radiuses
   */
  static calculatePointAlongSegmentList(segmentList, distance) {
    var curDistanceAlong = 0
    for(var segment of segmentList) {
      let segLength = segment.getLength()
      if(distance >= curDistanceAlong && distance <= curDistanceAlong+segLength && segLength > 0) {
        return segment.getPoint((distance - curDistanceAlong) / segLength)
      }
      curDistanceAlong += segLength
    }

    return null
  }


  /**
   * Calculate the length of a segment list
   * Currently ignores corner radiuses
   * Returns total distance of the segment list
   */
  static calculateSegmentListLength(segmentList) {
    return segmentList.reduce((curDistance, segment) => curDistance + segment.getLength(), 0)
  }



  /**
   * Attempt to connect an array of GlassSegment into a valid path from start to finish
   * Assumes the path is NOT closed
   * Uses start of 1st segment as the starting point
   *   
   * Returns null if a valid path cannot be made with all segments
   * Returns the resulting array of connected segments if successful
   */
  static connectSegmentListToPath(segmentList, tolerance = 0.01) {
    if(segmentList.length <= 1)
      return segmentList

    var sourceList = segmentList.map(seg => seg.clone())
    var resultPath = sourceList.splice(0, 1)
    var foundConnection = true
    while(foundConnection) {
      foundConnection = false
      var lastPnt = resultPath[resultPath.length-1].endPosition

      for(var i=0; i<sourceList.length; i++) {
        if(sourceList[i].startPosition.distanceTo(lastPnt) <= tolerance) {
          resultPath = resultPath.concat(sourceList.splice(i, 1))
          foundConnection = true
          break
        }
      }
    }

    if(sourceList.length > 0)
      return null
    return resultPath
  }


  /**
   * Calculate the total bounds of a list of Box2 objects
   * Returns a Box2 object containing bounds of all Box2 objects
   */
  static calculateBoundsForBoxList(boxList) {
    var bounds = new THREE.Box2()
    if(boxList == null)
      return bounds

    boxList.forEach(box => {
      bounds.union(box)
    })

    return bounds

  }


  /**
   * Calculate the bounding box of an array of 2D points
   * Returns a Box2 object containing bounds of all the points
   */
  static calculate2DPointListBounds(pointList) {
    var bounds = new THREE.Box2()

    pointList.forEach(point => {
      bounds.expandByPoint(point)
    })
    return bounds
  }


  /**
   * Calculate the path length of a list of 2D points
   * Assumes all points are straight line points & the path is not closed
   */
  static calculate2DPointListLength(pointList) {
    var totalLength = 0

    var prevPnt = null
    pointList.forEach(point => {
      if(prevPnt)
        totalLength += prevPnt.distanceTo(point)

      prevPnt = point
    })

    return totalLength
  }


  /**
   * Sort an array of 2D points by a direction
   */
  static sort2DPointListByDirection(pointList, direction) {
    if(pointList.length < 2)
      return pointList

    var sortedList = pointList
    sortedList.sort((pntA, pntB) => {
      return MathsUtils.calculateDistanceToPlane2D(direction, pointList[0], pntA) - MathsUtils.calculateDistanceToPlane2D(direction, pointList[0], pntB)
    })
    return sortedList
  }


  /**
   * Check if a 2D point is on the edge of the bounds given by Box2 object
   * Allows tolerance of distance from point to bound edge
   *
   */
  static pointOnBoundsEdge(point, bounds, tolerance = 0.1) {
    let isEdgeMinX = Math.abs(point.x - bounds.min.x) < tolerance
    let isEdgeMaxX = Math.abs(point.x - bounds.max.x) < tolerance
    let isEdgeMinY = Math.abs(point.y - bounds.min.y) < tolerance
    let isEdgeMaxY = Math.abs(point.y - bounds.max.y) < tolerance
    
    let isXInside = (point.x >= bounds.min.x-tolerance) && (point.x <= bounds.max.x+tolerance)
    let isYInside = (point.y >= bounds.min.y-tolerance) && (point.y <= bounds.max.y+tolerance)

    if((isEdgeMinX || isEdgeMaxX) && isYInside)
      return true
    if((isEdgeMinY || isEdgeMaxY) && isXInside)
      return true
    return false
  }


  /**
   * Rotate a 2D point around an origin by an angle in radians
   */
  static rotate2DPoint(origin, point, angleRadians) {
    var x = point.x - origin.x
    var y = point.y - origin.y

    var xRot = x * Math.cos(angleRadians) - y * Math.sin(angleRadians)
    var yRot = y * Math.cos(angleRadians) + x * Math.sin(angleRadians)

    return new THREE.Vector2(origin.x + xRot, origin.y + yRot)
  }


  /**
   * Rotate a segment list around an origin by an angle in radians
   */
  static rotateSegmentList(segmentList, origin, angleRadians) {
    segmentList.forEach(segment => {
      segment.startPosition = MathsUtils.rotate2DPoint(origin, segment.startPosition, angleRadians)
      segment.endPosition = MathsUtils.rotate2DPoint(origin, segment.endPosition, angleRadians)

      if(segment.type == SegmentTypes.arc) {
        segment.arcCenterPosition = MathsUtils.rotate2DPoint(origin, segment.arcCenterPosition, angleRadians)
        segment.arcStartAngle = MathsUtils.calculateArcPointAngle(segment.arcCenterPosition, segment.startPosition)
        segment.arcEndAngle = MathsUtils.calculateArcPointAngle(segment.arcCenterPosition, segment.endPosition)

        segment.arcUpdated()
      }
    })

    segmentList.forEach((segment, segmentIndex) => {
      let prevSeg = segmentList[segmentIndex == 0 ? segmentList.length-1 : segmentIndex-1]
      let nextSeg = segmentList[(segmentIndex + 1) % segmentList.length]

      // Update the corner radius details
      segment.startCornerRadiusDelta = prevSeg.startPosition.clone().sub(segment.startPosition)
      segment.endCornerRadiusDelta = nextSeg.endPosition.clone().sub(segment.endPosition)
    })
  }


  /**
   * Round the positions in a segment list by n decimal places
   */
  static roundSegmentListPositions(segmentList, places = 4) {
    segmentList.forEach(segment => {
      segment.startPosition = MathsUtils.round2DVector(segment.startPosition, places)
      segment.endPosition = MathsUtils.round2DVector(segment.endPosition, places)

      if(segment.type == SegmentTypes.arc) {
        segment.arcCenterPosition = MathsUtils.round2DVector(segment.arcCenterPosition, places)
        segment.arcUpdated()
      }
    });

    // Update the corner radius details
    segmentList.forEach((segment, segmentIndex) => {
      let prevSeg = segmentList[segmentIndex == 0 ? segmentList.length-1 : segmentIndex-1]
      let nextSeg = segmentList[(segmentIndex + 1) % segmentList.length]

      segment.startCornerRadiusDelta = prevSeg.startPosition.clone().sub(segment.startPosition)
      segment.endCornerRadiusDelta = nextSeg.endPosition.clone().sub(segment.endPosition)
    })
  }


  /**
   * Filter consecutive duplicates out from a list of 2D points
   * Duplicates if the x & y values are within the threshold value on each axis
   * 
   * Returns an array of Vector2 objects without duplicates
   */
  static removeConsecutive2DPointDuplicates(pointList, threshold = 0.0001) {
    if(pointList.length <= 1)
      return pointList

    var uniqueList = [pointList[0]]
    var lastPoint = pointList[0]
    for(var i=1; i<pointList.length; i++) {
      let point = pointList[i]
      if(Math.abs(lastPoint.x - point.x) > threshold || Math.abs(lastPoint.y - point.y) > threshold)
        uniqueList.push(point)
      lastPoint = point
    }

    return uniqueList
  }


  /**
   * Optimize a point list removing redundant points that do no alter the point list path
   * 
   * Returns an array of Vector2 objects that are optimized
   */
  static optimizePointList(pointList, options = {}) {
    _.defaults(options, {
      optimizeDuplicatePoints: true,
      optimizeLines: true,
      angleThreshold: 0.1,
      duplicateThreshold: 0.0001,
      pointTransformCallback: null
    })
    var optimizedList = pointList

    // Check angles between every 3 point group
    // If the center point is inline by the angle threshold, remove it & start again
    var pointsRemoved = true
    while(pointsRemoved) {
      pointsRemoved = false

      for(var i=0; i<optimizedList.length+2 && !pointsRemoved && optimizedList.length >= 3; i++) {
        var prev = optimizedList[i % optimizedList.length]
        var mid = optimizedList[(i+1) % optimizedList.length]
        var next = optimizedList[(i+2) % optimizedList.length]

        if(options.pointTransformCallback) {
          prev = options.pointTransformCallback(prev)
          mid = options.pointTransformCallback(mid)
          next = options.pointTransformCallback(next)
        }

        let angle1 = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(Math.atan2(mid.y - prev.y, mid.x - prev.x)))
        let angle2 = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(Math.atan2(next.y - mid.y, next.x - mid.x)))
        let angleDiff = Math.abs(angle1 - angle2)

        // Duplicate points?
        if(options.optimizeDuplicatePoints) {
          if((Math.abs(prev.x - mid.x) < options.duplicateThreshold && Math.abs(prev.y - mid.y) < options.duplicateThreshold) ||
             (Math.abs(next.x - mid.x) < options.duplicateThreshold && Math.abs(next.y - mid.y) < options.duplicateThreshold)) {         

            optimizedList.splice((i+1) % optimizedList.length, 1)
            pointsRemoved = true
            continue
          }
        }

        // Same angles but not near each other?
        if(options.optimizeLines) {
          if(angleDiff < options.angleThreshold) {
            optimizedList.splice((i+1) % optimizedList.length, 1)
            pointsRemoved = true
            continue
          }
        }
      }
    }

    return optimizedList
  }


  /**
   * Check if 2 2D points are equal within a threshold
   * Returns true if the points are close
   */
  static check2DPointsEqual(pointA, pointB, threshold = 0.00001) {
    return Math.abs(pointA.x - pointB.x) <= threshold && Math.abs(pointA.y - pointB.y) <= threshold
  }


  /**
   * Check if a 2D point is in an array list
   * Returns true if a point is already in the list with same coordinates within a threshold
   */
  static pointInList(pointList, point, threshold = 0.00001) {
    return pointList.find(listPoint => {
      return MathsUtils.check2DPointsEqual(listPoint, point, threshold)
    }) != null
  }


  /**
   * Calculate the signed area of a 2D triangle made up from 3 2D points
   */
  static calculateSignedArea(point1, point2, point3) {
    return (point2.y - point1.y) * (point3.x - point2.x) - (point2.x - point1.x) * (point3.y - point2.y)
  }


  /**
   * Check if 3 2D points are collinear
   * Returns true if all 3 points are on the same line
   * 
   * NOTE: Not 100% sure this works correctly
   */
  static check2DPointsCollinear(pointA, pointB, pointC) {
    // Calculates tolerance based on point space. Accounts for very large numbers
    let tolerance = Math.abs(Math.max(pointA.x, pointB.x, pointC.x, pointA.y, pointB.y, pointC.y)) * 0.000001
    let value = Math.abs((pointB.x - pointA.x) * (pointC.y - pointA.y) - (pointC.x - pointA.x) * (pointB.y - pointA.y))

    return value < tolerance
  }


  /**
   * Check if a 2D point rests on a 2D segment
   */
  static point2DOnLineSegment(startPoint, endPoint, point, tolerance = 0.0001) {
    // If not even close to the line, exit early
    if(Math.min(startPoint.x, endPoint.x) - tolerance > point.x || Math.max(startPoint.x, endPoint.x) + tolerance < point.x ||
       Math.min(startPoint.y, endPoint.y) - tolerance > point.y || Math.max(startPoint.y, endPoint.y) + tolerance < point.y) {
      return false
    }

    let ap = new THREE.Vector2(point.x - startPoint.x, point.y - startPoint.y)
    let ab = new THREE.Vector2(endPoint.x - startPoint.x, endPoint.y - startPoint.y)
    let precisionPlaces = 6   // JB: Fixes rounding where points are just below/after the segment by 1/10000000 mm

    let lineLengthSq = ab.lengthSq()
    if(lineLengthSq == 0.0)
      return false

    let distance = ap.dot(ab) / lineLengthSq
    if(round(distance, precisionPlaces) < 0)
      return false
    else if(round(distance, precisionPlaces) > 1)
      return false

    // Interpolate along segment
    let projected = startPoint.clone().add(ab.multiplyScalar(distance))
    let dist = projected.distanceTo(point)
    return dist < tolerance
  }


  /**
   * Check if a 2D point rests on a 2D arc
   */
  static point2DOnArc(arcCentrePos, arcRadius, arcStartAngle, arcEndAngle, arcClockwise, point, tolerance = 0.0001) {

    var dist = point.distanceTo(arcCentrePos)
    if(Math.abs(dist - arcRadius) > tolerance)
      return false

    var intAngle = MathsUtils.calculateArcPointAngle(arcCentrePos, point)

    return MathsUtils.angleWithinRangeDegrees(arcStartAngle, arcEndAngle, intAngle, arcClockwise)
  }


  /**
   * Check if a 2D point rests on an edge of a 2D bounding box
   * Bounds edgeIndex is provided and must be between 0 -> 3
   */
  static point2DOnBoundsEdgeSegment(bounds, edgeIndex, point, tolerance = 0.0001) {
    if(edgeIndex < 0 || edgeIndex > 3)
      return false
    
    let boundsStartPoint = new THREE.Vector2(0, 0)
    let boundsEndPoint = new THREE.Vector2(0, 0)

    // Edges start from top & go clockwise
    switch(edgeIndex) {
      // Top
      case 0:
        boundsStartPoint.set(bounds.min.x, bounds.min.y)
        boundsEndPoint.set(bounds.max.x, bounds.min.y)
        break
      // Right
      case 1:
        boundsStartPoint.set(bounds.max.x, bounds.min.y)
        boundsEndPoint.set(bounds.max.x, bounds.max.y)
        break
      // Bottom
      case 2:
        boundsStartPoint.set(bounds.max.x, bounds.max.y)
        boundsEndPoint.set(bounds.min.x, bounds.max.y)
        break
      // Left
      case 3:
        boundsStartPoint.set(bounds.min.x, bounds.max.y)
        boundsEndPoint.set(bounds.min.x, bounds.min.y)
        break
    }

    return MathsUtils.point2DOnLineSegment(boundsStartPoint, boundsEndPoint, point, tolerance)
  }


  /**
   * Calculate the distance of a list of points along a vector
   * Calculates total 1D bound along the directional vector
   */
  calculatePointListDistanceForDirection(pointList, direction, relativePoint = null) {
    if(pointList.length == 0)
      return 0

    let rangeInfo = MathsUtils.calculatePointListDistanceRangeForDirection(pointList, direction, relativePoint)
    return rangeInfo.max - rangeInfo.min
  }


  /**
   * Calculate the min/max ranges of a list of points along a vector
   * Returns an object with min/max properties containing distance along the vector
   * Calculated relative to the 1st point or a provided point
   */
  static calculatePointListDistanceRangeForDirection(pointList, direction, relativePoint = null) {
    let range = {
      min: 0,
      max: 0
    }
    if(pointList.length == 0)
      return range

    pointList.forEach(point => {
      let dist = MathsUtils.calculateDistanceToPlane2D(direction, relativePoint || pointList[0], point)
      range.min = Math.min(range.min, dist)
      range.max = Math.max(range.max, dist)
    })

    return range
  }


  /**
   * Calculate the min/max ranges of a list of segments along a vector
   * Returns an object with min/max properties containing distance along the vector
   * Calculated relative to the 1st point or a provided point
   */
  static calculateSegmentListDistanceRangeForDirection(segmentList, direction, relativePoint = null) {

    var pointList = []
    segmentList.forEach(segment => {
      pointList.push(segment.startPosition)
      pointList.push(segment.getMidpoint())
      pointList.push(segment.endPosition)
    })

    return MathsUtils.calculatePointListDistanceRangeForDirection(pointList, direction, relativePoint)
  }


  /**
   * Calculate the circle details given 3 points the circle must pass through
   * Based on common maths code for circle for 3 points online
   * 
   * Returns an object containing radius and center position (Vector2)
   * Returns null if the circle equation for the 3 points cannot be solved
   */
  static calculateCircleInfoForPoints(pointA, pointB, pointC) {

    let d1 = new THREE.Vector2(pointB.y - pointA.y, pointA.x - pointB.x)
    let d2 = new THREE.Vector2(pointC.y - pointA.y, pointA.x - pointC.x)
    let k = d2.x * d1.y - d2.y * d1.x
    if(Math.abs(k) < 0.00001)
      return null

    let s1 = new THREE.Vector2((pointA.x + pointB.x) / 2.0, (pointA.y + pointB.y) / 2.0)
    let s2 = new THREE.Vector2((pointA.x + pointC.x) / 2.0, (pointA.y + pointC.y) / 2.0)
    let l = d1.x * (s2.y - s1.y) - d1.y * (s2.x - s1.x)
    let m = l / k
    let center = new THREE.Vector2(s2.x + m * d2.x, s2.y + m * d2.y)
    let dx = center.x - pointA.x
    let dy = center.y - pointA.y
    let radius = Math.sqrt(dx * dx + dy * dy)

    return {
      position: center,
      radius: radius
    }
  }


  /**
   * Calculate the circle details given 2 points and a radius
   * Based on common maths code for circle through 2 points + radius online
   * 
   * Returns an array of position solutions containing radius and center position (Vector2)
   * Returns an empty array if no solutions (or infinity number of solutions) are found
   */
  static calculateCircleInfoForPointsRadius(pointA, pointB, radius) {
    let separation = pointA.distanceTo(pointB)

    if(separation == 0.0) {
      return []
    } else if(separation == 2.0 * radius) {
      // Midpoint of the points
      return [{
        position: new THREE.Vector2((pointA.x + pointB.x) / 2.0, (pointA.y + pointB.y) / 2.0),
        radius: radius
      }]

    } else if(separation > 2.0 * radius) {
      // Points too far away from radius
      return []

    } else {
      // 2 solutions
      let mirrorDistance = Math.sqrt(Math.pow(radius, 2.0) - Math.pow(separation/2.0, 2.0))

      let centerA = new THREE.Vector2(
        (pointA.x + pointB.x)/2 + mirrorDistance*(pointA.y - pointB.y)/separation,
        (pointA.y + pointB.y)/2 + mirrorDistance*(pointB.x - pointA.x)/separation
      )
      let centerB = new THREE.Vector2(
        (pointA.x + pointB.x)/2 - mirrorDistance*(pointA.y - pointB.y)/separation,
        (pointA.y + pointB.y)/2 - mirrorDistance*(pointB.x - pointA.x)/separation
      )

      return [
        { position: centerA, radius: radius },
        { position: centerB, radius: radius }
      ]
    }
  }


  /**
   * Generate a list of 2D points for a rotated rectangle
   * Point list is NOT closed, and is clockwise winding
   */
  static generateRotatedRectanglePolygon(angleDegrees, centerPoint, width, height) {
    var pointList = []

    let rads = MathsUtils.degreesToRadians(angleDegrees)
    let fwdDir = MathsUtils.generate2DDirectionVector(rads - Math.PI / 2)
    let rightDir = MathsUtils.generate2DDirectionVector(rads)

    pointList.push(centerPoint.clone().addScaledVector(fwdDir, height / 2).addScaledVector(rightDir, -width / 2))
    pointList.push(centerPoint.clone().addScaledVector(fwdDir, height / 2).addScaledVector(rightDir, width / 2))
    pointList.push(centerPoint.clone().addScaledVector(fwdDir, -height / 2).addScaledVector(rightDir, width / 2))
    pointList.push(centerPoint.clone().addScaledVector(fwdDir, -height / 2).addScaledVector(rightDir, -width / 2))

    return pointList
  }


  /**
   * Project 2 line segments out and calculate where they would intersect (if at all)
   * Returns null if no intersection occured (ie, paralell lines)
   */
  static projectLineIntersection(seg1Start, seg1End, seg2Start, seg2End) {
    let at = (seg2End.x - seg2Start.x) * (seg1Start.y - seg2Start.y) - (seg2End.y - seg2Start.y) * (seg1Start.x - seg2Start.x);
    let b  = (seg2End.y - seg2Start.y) * (seg1End.x - seg1Start.x) - (seg2End.x - seg2Start.x) * (seg1End.y - seg1Start.y);

    if(b != 0) {
      let ua = at / b;

      return new THREE.Vector2(seg1Start.x + ua * (seg1End.x - seg1Start.x), seg1Start.y + ua * (seg1End.y - seg1Start.y))
    }
    return null
  }


  /**
   * Calculate the midpoint position of a 2D line
   */
  static calculateLineMidpoint(startPoint, endPoint) {
    return new THREE.Vector2((startPoint.x + endPoint.x) / 2.0, (startPoint.y + endPoint.y) / 2.0)
  }


  /**
   * Calculate the direction vector from a 2D line (start -> end)
   * Returns normalized vector2
   */
  static calculateLineDirection(startPoint, endPoint) {
    let dir = endPoint.clone().sub(startPoint)
    dir.normalize()
    return dir
  }


  /**
   * Calculate the outwards direction vector from a 2D line (start -> end)
   * Returns normalized vector2
   */
  static calculateLineOutDirection(startPoint, endPoint) {
    let dir = endPoint.clone().sub(startPoint)
    dir.normalize()
    return new THREE.Vector2(dir.y, -dir.x)
  }


  /**
   * Wrap a circular value if required so it's between 0-max
   */
  static wrapValue(value, max) {
    if(isNaN(value))
      return value

    while(value < 0)
      value += max
    while(value >= max)
      value -= max
    return value
  }


  /**
   * Check if a line segment between 2 points is axis aligned
   */
  static isLineAxisAligned(startPoint, endPoint, threshold = 0.1) {
    if(Math.abs(startPoint.x - endPoint.x) < threshold)
      return true
    if(Math.abs(startPoint.y - endPoint.y) < threshold)
      return true

    return false
  }


  /**
   * Check if a value is between a start/end value on a circular range
   * Range is inclusive of min/max
   * For example, if a point index on a closed polygon is between start/end indices
   */
  static isValueBetweenCircularValues(value, min, max) {
    if(min > max)
      return (value <= max || value >= min)

    return (value >= min && value <= max)
  }


  /**
   * Calculate the section of a bounding box a point resides in using the AlignmentTypes
   * The bounding box is split into a 3x3 grid of 9 cells.
   * Returns an object with horizontal & vertical types
   */
  static calculatePointInBoundsAlignmentType(bounds, point) {
    var alignment = {
      horizontal: AlignmentTypes.center,
      vertical: AlignmentTypes.middle
    }
    if(bounds.isEmpty())
      return alignment

    var boundsWidth = bounds.max.x - bounds.min.x
    var boundsHeight = bounds.max.y - bounds.min.y
    var posX = point.x - bounds.min.x
    var posY = point.y - bounds.min.y

    if(boundsWidth <= 0 || boundsHeight <= 0)
      return alignment

    if(posX < boundsWidth * 0.33)
      alignment.horizontal = AlignmentTypes.left
    else if(posX > boundsWidth * 0.66)
      alignment.horizontal = AlignmentTypes.right
    if(posY < boundsHeight * 0.33)
      alignment.vertical = AlignmentTypes.top
    else if(posY > boundsHeight * 0.66)
      alignment.vertical = AlignmentTypes.bottom

    return alignment
  }


  /**
   * Calculate the section of a bounding box a point resides in using the AlignmentTypes
   * The bounding box is split into a 2x2 grid of 4 cells using only the corners
   * Returns an object with horizontal & vertical types
   */
  static calculatePointInBoundsAlignmentTypeByCorner(bounds, point) {
    var alignment = {
      horizontal: AlignmentTypes.left,
      vertical: AlignmentTypes.top
    }
    if(bounds.isEmpty())
      return alignment

    var boundsWidth = bounds.max.x - bounds.min.x
    var boundsHeight = bounds.max.y - bounds.min.y
    var posX = point.x - bounds.min.x
    var posY = point.y - bounds.min.y

    if(boundsWidth <= 0 || boundsHeight <= 0)
      return alignment

    if(posX < boundsWidth/2)
      alignment.horizontal = AlignmentTypes.left
    else
      alignment.horizontal = AlignmentTypes.right
    if(posY < boundsHeight/2)
      alignment.vertical = AlignmentTypes.top
    else
      alignment.vertical = AlignmentTypes.bottom

    return alignment
  }


  /**
   * Alter a segment list by shrinking or expanding it from the center of the shape
   * Minus offset value causes the segment list to reduce in size
   * Returns a copy of the list
   */
  static expandSegmentList(segmentList, offsetDistance) {
    var outputSegmentList = segmentList.map(segment => segment.clone())

    // Shrink / Grow
    outputSegmentList.forEach((segment, segmentIndex) => {
      let prevSeg = outputSegmentList[segmentIndex == 0 ? outputSegmentList.length-1 : segmentIndex-1]

      let prevOutDir = prevSeg.getOutDirection()
      let curOutDir = segment.getOutDirection()

      let bisectDir = new THREE.Vector2(prevOutDir.x + curOutDir.x, prevOutDir.y + curOutDir.y)
      bisectDir.normalize()

      // Compute angle between normals to scale inset correctly
      let scale = offsetDistance / Math.sqrt((1 + curOutDir.x * prevOutDir.x + curOutDir.y * prevOutDir.y) / 2)

      prevSeg.endPosition.addScaledVector(bisectDir, scale)
      segment.startPosition.addScaledVector(bisectDir, scale)

      // If an arc, move arc center point directly in/out & reduce radius
      if(segment.type == SegmentTypes.arc) {
        segment.arcCenterPosition.addScaledVector(curOutDir, offsetDistance)
        segment.arcRadius -= offsetDistance
        segment.arcUpdated()
      }
    })

    MathsUtils.segmentListUpdated(outputSegmentList)

    return outputSegmentList
  }


  /**
   * Calculate the total perimeter based on an array of segments
   * Returns length in millimetres
   */
  static calculateSegmentListPerimeter(segmentList, arcSubdivisions = 20) {
    var outlineShape = MathsUtils.generateShapeFromSegmentList(segmentList, { isHole: false })

    var totalLength = 0
    
    // Based on shape to include corner radiuses
    var outlinePoints = outlineShape.getPoints(arcSubdivisions)

    for(var i=0; i<outlinePoints.length; i++) {
      let nextPoint = outlinePoints[(i + 1) % outlinePoints.length]
      totalLength += outlinePoints[i].distanceTo(nextPoint)
    }
    return totalLength
  }


  /**
   * Calculate the total area based on an array of segments
   * Returns a value in square millimetres
   */
  static calculateSegmentListArea(segmentList, arcSubdivisions = 20) {
    var outlineShape = MathsUtils.generateShapeFromSegmentList(segmentList, { isHole: false })
    var outlinePoints = outlineShape.getPoints(arcSubdivisions)

    var area = 0
    outlinePoints.forEach((point, index) => {
      let prevIndex = index == 0 ? outlinePoints.length-1 : index-1
      area += (outlinePoints[prevIndex].x + point.x) * (outlinePoints[prevIndex].y - point.y)
    })
    area = Math.abs(area / 2.0)

    return area
  }



  /**
   * Serialize a THREE.JS Vector2 object into a raw object
   * Used for persistance & undo system
   */
  static serializeVector2(vec) {
    return {
      x: vec.x,
      y: vec.y
    }
  }
  
  /**
   * Deserializes an object into a Vector2 object
   * Used for persistance & undo system
   */
  static deserializeVector2(obj) {
    if(obj == null)
      return new THREE.Vector2() 
    return new THREE.Vector2(parseFloat(obj.x || 0), parseFloat(obj.y || 0))
  }


  /**
   * Serialize a THREE.JS Vector3 object into a raw object
   * Used for persistance & undo system
   */
   static serializeVector3(vec) {
    return {
      x: vec.x,
      y: vec.y,
      z: vec.z
    }
  }
  
  /**
   * Deserializes an object into a Vector2 object
   * Used for persistance & undo system
   */
  static deserializeVector3(obj) {
    if(obj == null)
      return new THREE.Vector3() 
    return new THREE.Vector3(parseFloat(obj.x || 0), parseFloat(obj.y || 0), parseFloat(obj.z || 0))
  }


  /**
   * Serialize a THREE.JS Box2 object into a raw object
   * Used for persistance & undo system
   */
  static serializeBox2(box) {
    return {
      min: { x: box.min.x, y: box.min.y },
      max: { x: box.max.x, y: box.max.y }
    }
  }


  /**
   * Deserializes an object into a THREE.JS Box2 object
   * Used for persistance & undo system
   */
  static deserializeBox2(obj) {
    if(obj == null)
      return new THREE.Box2() 

    return new THREE.Box2(
      new THREE.Vector2(parseFloat(obj.min.x || 0), parseFloat(obj.min.y || 0)),
      new THREE.Vector2(parseFloat(obj.max.x || 0), parseFloat(obj.max.y || 0))
    )
  }
}